import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./steps.scss";
import axios from "axios";
import { Api_Url } from "../../../../utils/ApiUrl";

const Basicinfo = ({
  basicInfo,
  setBasicInfo,
  projectId,
  isAvailable,
  setIsAvailable,
}) => {
  const [tickerChecking, setTickerChecking] = useState(false);
  const [tickerAvailable, setTickerAvailable] = useState(null);
  const [tickerDebounceTimeout, setTickerDebounceTimeout] = useState(null);
  const [originalTicker, setOriginalTicker] = useState(""); // Track original ticker

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "link",
    "image",
  ];

  // Store original ticker when component mounts or projectId changes
  useEffect(() => {
    if (projectId && basicInfo?.projectTicker?.value) {
      setOriginalTicker(basicInfo.projectTicker.value);
    }
  }, [projectId]);

  const handleChange = (field, value) => {
    setBasicInfo((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        value,
        error: false,
        errorMsg: "",
      },
    }));
  };

  // Check ticker availability
  const checkTickerAvailability = async (ticker) => {
    console.log(
      "Checking ticker availability for:",
      projectId,
      originalTicker,
      ticker
    );
    if ((projectId && originalTicker !== "") || !projectId) {
      if (!ticker || ticker.trim() === "") {
        setTickerAvailable(null);
        return;
      }

      console.log(
        "projectId?.toLowerCase()",
        ticker?.toLowerCase().trim(),
        "originalTicker",
        originalTicker?.toLowerCase().trim(),
        ticker?.toLowerCase().trim() === originalTicker?.toLowerCase().trim()
      );

      // Skip validation if editing project and ticker hasn't changed
      if (
        projectId &&
        ticker?.toLowerCase().trim() === originalTicker?.toLowerCase().trim()
      ) {
        setTickerAvailable(true);
        return;
      }

      setTickerChecking(true);
      let tok = localStorage.getItem("accessToken");

      try {
        const response = await axios.get(
          `${Api_Url}/pro-projects/check/${ticker.trim()}`,
          {
            headers: {
              Authorization: `Bearer ${tok}`,
            },
          }
        );

        const isExists = response.data?.data?.isTickerExists;
        setTickerAvailable(!isExists);

        if (isExists) {
          setIsAvailable(false);
          setBasicInfo((prev) => ({
            ...prev,
            projectTicker: {
              ...prev.projectTicker,
              error: true,
              errorMsg: "This ticker is already taken",
            },
          }));
        } else {
          // Clear error if ticker is available
          setIsAvailable(true);
          setBasicInfo((prev) => ({
            ...prev,
            projectTicker: {
              ...prev.projectTicker,
              error: false,
              errorMsg: "",
            },
          }));
        }
      } catch (error) {
        console.error("Error checking ticker:", error);
        setTickerAvailable(null);
      } finally {
        setTickerChecking(false);
      }
    }
  };

  // Debounce ticker check
  useEffect(() => {
    const ticker = basicInfo?.projectTicker?.value;

    // Clear previous timeout
    if (tickerDebounceTimeout) {
      clearTimeout(tickerDebounceTimeout);
    }

    // Reset availability state when user is typing
    setTickerAvailable(null);

    // Set new timeout for checking
    if (ticker && ticker.trim() !== "") {
      const timeout = setTimeout(() => {
        checkTickerAvailability(ticker);
      }, 800); // Wait 800ms after user stops typing

      setTickerDebounceTimeout(timeout);
    }

    // Cleanup
    return () => {
      if (tickerDebounceTimeout) {
        clearTimeout(tickerDebounceTimeout);
      }
    };
  }, [basicInfo?.projectTicker?.value, originalTicker]);

  return (
    <>
      <div className="parentsteps">
        <section className="inner_steps">
          <h2>
            Project Name<span>*</span>
          </h2>
          <input
            onChange={(e) => handleChange("projectName", e.target.value)}
            type="text"
            value={basicInfo?.projectName?.value || ""}
            className={basicInfo?.projectName?.error ? "error" : ""}
            placeholder="Enter project name"
          />
          {basicInfo.projectName.error && (
            <span className="error-text">{basicInfo.projectName.errorMsg}</span>
          )}

          <h2>
            Project Ticker<span>*</span>
          </h2>
          <div style={{ position: "relative" }}>
            <input
              value={basicInfo?.projectTicker?.value || ""}
              onChange={(e) => handleChange("projectTicker", e.target.value)}
              className={basicInfo?.projectTicker?.error ? "error" : ""}
              type="text"
              placeholder="Enter project ticker"
            />
            {tickerChecking && (
              <span
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#939FA3",
                  fontSize: "14px",
                }}
              >
                Checking...
              </span>
            )}
            {!tickerChecking && tickerAvailable === true && (
              <span
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#329879",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#329879"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                Available
              </span>
            )}
          </div>
          {basicInfo.projectTicker.error && (
            <span className="error-text">
              {basicInfo.projectTicker.errorMsg}
            </span>
          )}

          <h2>
            Project Short Description<span>*</span>
          </h2>
          <textarea
            placeholder="Project Short Description"
            value={basicInfo?.shortDescription?.value || ""}
            className={basicInfo?.shortDescription?.error ? "error" : ""}
            onChange={(e) => handleChange("shortDescription", e.target.value)}
            rows="4"
            cols="50"
            maxLength={150}
          ></textarea>
          {basicInfo.shortDescription.error && (
            <span className="error-text">
              {basicInfo.shortDescription.errorMsg}
            </span>
          )}
          <h3>Less then 150 Characters</h3>

          <h2 className="projectsdetails">
            Project Details<span>*</span>
          </h2>
          <div style={{ margin: "20px 0" }}>
            <ReactQuill
              className={
                "editorrr " + (basicInfo?.details?.error ? "error" : "")
              }
              theme="snow"
              value={basicInfo?.details?.value || ""}
              onChange={(value) =>
                setBasicInfo((prev) => ({
                  ...prev,
                  ["details"]: {
                    ...prev["details"],
                    value,
                    error: false,
                    errorMsg: "",
                  },
                }))
              }
              modules={modules}
              formats={formats}
              placeholder="Project Details"
              style={{ height: "112px" }}
            />
            {basicInfo.details.error && (
              <span className="error-text">{basicInfo.details.errorMsg}</span>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default Basicinfo;
